// Deficiency export — in-browser.
//
// Deficiencies come from the live audit engine (/v1/audit) per property, falling
// back to the legacy property_deficiencies_and_recommendations blob for
// properties not yet on the new schema during rollout. Portfolio exports fan out
// per-property fetches with bounded concurrency.

import { supabase } from "@/shared/integrations/supabase/client";
import { selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import { getAuditSite } from "@/features/audit/api";
import { normalizeAuditSite } from "@/features/audit/normalizeAudit";
import { isNotFound } from "@/features/audit/useAuditDeficiencies";
import type { Json } from "@/shared/integrations/supabase/types";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import { resolveSurveyAttributes } from "@/features/site-survey-editor/services/resolveSurveyAttributes";
import * as XLSX from "xlsx-js-style";
import type { ExportFormat } from "./types";
import { chunk, csvCell, sanitize, triggerDownload } from "./_helpers";
import { downloadWorkbook } from "./_xlsx";

interface ExportDeficienciesInput {
  projectId: string;
  label: string;
  propertyId?: string;
  format: ExportFormat;
}

interface PropertyLite {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
}

interface RawDeficiency {
  id?: string | null;
  severity?: string | null;
  group?: string | null;
  title?: string | null;
  detail?: string | null;
  recommendation_summary?: string | null;
  survey_field?: string | null;
  survey_field_id?: string | null;
  asset?: {
    asset_id?: string | null;
    name?: string | null;
    category?: string | null;
  } | null;
  additional_survey_field_ids?: unknown;
}

interface DeficiencyPayload {
  deficiencies?: RawDeficiency[];
}

interface DeficiencySourceRow {
  property_id: string;
  data: Json;
}

interface DeficiencyExportRow {
  propertyId: string;
  propertyLabel: string;
  category: string;
  title: string;
  severity: string;
  source: string;
  detail: string;
  recommendation: string;
  asset: string;
  assetCategory: string;
  surveyField: string;
  imageUrl: string;
}

interface AssetPhotoRow {
  asset_id: string;
  file_path: string | null;
}

const CSV_HEADERS = [
  "Property",
  "Category",
  "Deficiency",
  "Severity",
  "Source",
  "Details",
  "Recommendation",
  "Asset",
  "Asset Category",
  "Survey Field",
  "Image URL",
] as const;

const PROPERTY_SHEET_HEADERS = [
  "Category",
  "Deficiency",
  "Severity",
  "Source",
  "Details",
  "Recommendation",
  "Asset",
  "Asset Category",
  "Survey Field",
  "Image URL",
] as const;

export async function exportDeficiencies(input: ExportDeficienciesInput): Promise<void> {
  const properties = await fetchProperties(input.projectId, input.propertyId);
  if (properties.length === 0) {
    throw new Error("No properties match the requested filters");
  }

  const propertyMap = new Map(properties.map((property) => [property.id, property]));
  const sourceRows = input.propertyId
    ? await fetchSinglePropertyDeficiencies(input.projectId, input.propertyId)
    : await fetchPortfolioDeficiencies(input.projectId, properties.map((property) => property.id));
  const imageContext = await fetchDeficiencyImageContext(sourceRows);
  const rows = flattenDeficiencies(sourceRows, propertyMap, imageContext);

  if (rows.length === 0) {
    throw new Error("No deficiencies found for the selected scope");
  }

  const safeLabel = sanitize(input.label).toLowerCase();
  const scope = input.propertyId ? "deficiencies" : "portfolio_deficiencies";
  const filename = `${safeLabel}_${scope}.${input.format}`;
  const isSingleProperty = Boolean(input.propertyId);

  if (input.format === "csv") {
    const csvRows = isSingleProperty
      ? [[...PROPERTY_SHEET_HEADERS], ...rows.map(toPropertySheetRow)]
      : [[...CSV_HEADERS], ...rows.map(toCsvRow)];
    const csv = csvRows.map((row) => row.map(csvCell).join(",")).join("\n");
    triggerDownload(csv, filename, "text/csv;charset=utf-8");
    return;
  }

  const workbook = XLSX.utils.book_new();
  if (isSingleProperty) {
    appendSheet(
      workbook,
      "Deficiencies",
      [[...PROPERTY_SHEET_HEADERS], ...rows.map(toPropertySheetRow)],
      [24, 42, 12, 14, 48, 48, 28, 22, 32, 72],
    );
    downloadWorkbook(workbook, filename);
    return;
  }

  appendSheet(workbook, "All Deficiencies", [[...CSV_HEADERS], ...rows.map(toCsvRow)], [
    24, 24, 42, 12, 14, 48, 48, 28, 22, 32, 72,
  ]);

  const rowsByProperty = new Map<string, DeficiencyExportRow[]>();
  for (const row of rows) {
    const propertyRows = rowsByProperty.get(row.propertyId) ?? [];
    propertyRows.push(row);
    rowsByProperty.set(row.propertyId, propertyRows);
  }

  for (const [propertyId, propertyRows] of rowsByProperty) {
    const propertyLabel = propertyRows[0]?.propertyLabel || propertyId;
    appendSheet(
      workbook,
      propertyLabel,
      [[...PROPERTY_SHEET_HEADERS], ...propertyRows.map(toPropertySheetRow)],
      [24, 42, 12, 14, 48, 48, 28, 22, 32, 72],
    );
  }

  downloadWorkbook(workbook, filename);
}

async function fetchProperties(projectId: string, propertyId?: string): Promise<PropertyLite[]> {
  return (
    await fetchClientProperties<PropertyLite>(
      projectId,
      "id, name, address, city, state",
      (q) => (propertyId ? q.eq("id", propertyId) : q),
    )
  ).sort((a, b) => {
    // name ascending, nulls last (matches the prior DB ordering)
    if (!a.name && !b.name) return 0;
    if (!a.name) return 1;
    if (!b.name) return -1;
    return a.name.localeCompare(b.name);
  });
}

const PROPERTY_BATCH = 40;

// Chunk sizes for the image-context `.in(...)` lookups. Portfolio-scale exports
// (an admin exporting a whole client) push thousands of ids through these, and a
// single unbounded `.in(...)` builds a request URL / response large enough to
// tear down the HTTP/2/QUIC connection ("Failed to fetch"). Each id stays wholly
// within one chunk, so the "latest row per id wins" ordering below is preserved.
// property_attributes rows carry heavy survey JSON, so they use a smaller batch.
const ASSET_ID_BATCH = 150;
const ATTRIBUTES_PROPERTY_BATCH = 40;

/**
 * One property's deficiencies — mirrors the Deficiencies tab: the live audit
 * engine first, falling back to the legacy blob when the property has no audit
 * findings yet (404 / 403 / empty) during rollout.
 */
async function fetchSinglePropertyDeficiencies(
  orgId: string,
  propertyId: string,
): Promise<DeficiencySourceRow[]> {
  try {
    const audit = normalizeAuditSite(await getAuditSite(orgId, propertyId));
    if (audit.deficiencies.length > 0) {
      return [
        { property_id: propertyId, data: { deficiencies: audit.deficiencies } as unknown as Json },
      ];
    }
  } catch (err) {
    if (!isNotFound(err)) throw err;
  }
  return fetchDeficiencyRows([propertyId]);
}

/** Read legacy property_deficiencies_and_recommendations blobs, chunked. */
async function fetchDeficiencyRows(propertyIds: string[]): Promise<DeficiencySourceRow[]> {
  const rows: DeficiencySourceRow[] = [];
  for (const idChunk of chunk(propertyIds, PROPERTY_BATCH)) {
    const { data, error } = await supabase
      .from("property_deficiencies_and_recommendations")
      .select("property_id, data")
      .in("property_id", idChunk);
    if (error) throw new Error(error.message || "Failed to fetch deficiencies");
    rows.push(...((data ?? []) as DeficiencySourceRow[]));
  }
  return rows;
}

/**
 * Portfolio (multi-property) deficiencies: the audit engine is per-property, so
 * fan out single-property fetches (audit-first, legacy fallback) with bounded
 * concurrency. A property whose fetch fails is skipped (contributes no rows) so
 * one bad property doesn't abort the whole export — unlike the single-property
 * path, which surfaces the error to the caller.
 */
async function fetchPortfolioDeficiencies(
  orgId: string,
  propertyIds: string[],
): Promise<DeficiencySourceRow[]> {
  const CONCURRENCY = 8;
  const rows: DeficiencySourceRow[] = [];
  for (let i = 0; i < propertyIds.length; i += CONCURRENCY) {
    const batch = propertyIds.slice(i, i + CONCURRENCY);
    const results = await Promise.all(
      batch.map(async (propertyId) => {
        try {
          return await fetchSinglePropertyDeficiencies(orgId, propertyId);
        } catch {
          // No audit data for this property → contribute no rows to the export.
          return [];
        }
      }),
    );
    for (const result of results) rows.push(...result);
  }
  return rows;
}

function flattenDeficiencies(
  sourceRows: DeficiencySourceRow[],
  propertyMap: Map<string, PropertyLite>,
  imageContext: DeficiencyImageContext,
): DeficiencyExportRow[] {
  return sourceRows.flatMap((sourceRow) => {
    const property = propertyMap.get(sourceRow.property_id);
    const payload = parseJson<DeficiencyPayload>(sourceRow.data, {});
    const deficiencies = Array.isArray(payload.deficiencies) ? payload.deficiencies : [];
    const propertyLabel = getPropertyLabel(property, sourceRow.property_id);

    return deficiencies.map((deficiency) => ({
      propertyId: sourceRow.property_id,
      propertyLabel,
      category: formatLabel(deficiency.group) || "General",
      title: deficiency.title || deficiency.asset?.name || deficiency.survey_field || "Deficiency",
      severity: formatLabel(deficiency.severity),
      source: deficiency.asset?.asset_id ? "Equipment" : deficiency.survey_field_id ? "Survey" : "Site",
      detail: deficiency.detail || "",
      recommendation: deficiency.recommendation_summary || "",
      asset: deficiency.asset?.name || "",
      assetCategory: deficiency.asset?.category || "",
      surveyField: deficiency.survey_field || "",
      imageUrl: getDeficiencyImageUrls(deficiency, sourceRow.property_id, imageContext).join("; "),
    }));
  }).sort((a, b) =>
    a.propertyLabel.localeCompare(b.propertyLabel) ||
    a.category.localeCompare(b.category) ||
    severityOrder(a.severity) - severityOrder(b.severity) ||
    a.title.localeCompare(b.title)
  );
}

function toCsvRow(row: DeficiencyExportRow): string[] {
  return [
    row.propertyLabel,
    row.category,
    row.title,
    row.severity,
    row.source,
    row.detail,
    row.recommendation,
    row.asset,
    row.assetCategory,
    row.surveyField,
    row.imageUrl,
  ];
}

function toPropertySheetRow(row: DeficiencyExportRow): string[] {
  return [
    row.category,
    row.title,
    row.severity,
    row.source,
    row.detail,
    row.recommendation,
    row.asset,
    row.assetCategory,
    row.surveyField,
    row.imageUrl,
  ];
}

interface DeficiencyImageContext {
  assetPhotosByAssetId: Map<string, string[]>;
  surveyFieldsByPropertyId: Map<string, Map<string, AttributeField>>;
}

async function fetchDeficiencyImageContext(sourceRows: DeficiencySourceRow[]): Promise<DeficiencyImageContext> {
  const assetIds = new Set<string>();
  const propertyIdsNeedingSurveyEvidence = new Set<string>();

  for (const sourceRow of sourceRows) {
    const payload = parseJson<DeficiencyPayload>(sourceRow.data, {});
    const deficiencies = Array.isArray(payload.deficiencies) ? payload.deficiencies : [];

    for (const deficiency of deficiencies) {
      if (deficiency.asset?.asset_id) {
        assetIds.add(deficiency.asset.asset_id);
      }
      if (getSurveyFieldIds(deficiency).length > 0) {
        propertyIdsNeedingSurveyEvidence.add(sourceRow.property_id);
      }
    }
  }

  const [assetPhotosByAssetId, surveyFieldsByPropertyId] = await Promise.all([
    fetchAssetPhotoUrls([...assetIds]),
    fetchSurveyFieldsByPropertyId([...propertyIdsNeedingSurveyEvidence]),
  ]);

  return { assetPhotosByAssetId, surveyFieldsByPropertyId };
}

async function fetchAssetPhotoUrls(assetIds: string[]): Promise<Map<string, string[]>> {
  const photosByAssetId = new Map<string, string[]>();
  if (assetIds.length === 0) return photosByAssetId;

  for (const idChunk of chunk(assetIds, ASSET_ID_BATCH)) {
    const { data, error } = await supabase
      .from("asset_documents")
      .select("asset_id, file_path")
      .is("deleted_at", null)
      .eq("document_type", "photo")
      .in("asset_id", idChunk)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message || "Failed to fetch deficiency image URLs");

    for (const photo of (data ?? []) as AssetPhotoRow[]) {
      const url = resolveAssetPhotoUrl(photo.file_path);
      if (!url) continue;

      const urls = photosByAssetId.get(photo.asset_id) ?? [];
      urls.push(url);
      photosByAssetId.set(photo.asset_id, urls);
    }
  }

  return photosByAssetId;
}

async function fetchSurveyFieldsByPropertyId(propertyIds: string[]): Promise<Map<string, Map<string, AttributeField>>> {
  const fieldsByPropertyId = new Map<string, Map<string, AttributeField>>();
  if (propertyIds.length === 0) return fieldsByPropertyId;

  const selectClause = await selectWithSurveySource("property_id, data, prod_data, captured_at");
  const latestByPropertyId = new Map<string, { data: Json; prodData: unknown; surveySource: string | null }>();

  for (const idChunk of chunk(propertyIds, ATTRIBUTES_PROPERTY_BATCH)) {
    const { data, error } = await supabase
      .from("property_attributes")
      .select(selectClause)
      .in("property_id", idChunk)
      .order("captured_at", { ascending: false });

    if (error) throw new Error(error.message || "Failed to fetch deficiency survey evidence");

    for (const row of (data ?? []) as unknown as Array<{ property_id: string; data: Json; prod_data?: unknown; survey_source?: string | null }>) {
      if (!latestByPropertyId.has(row.property_id)) {
        latestByPropertyId.set(row.property_id, { data: row.data, prodData: row.prod_data, surveySource: row.survey_source ?? null });
      }
    }
  }

  // Resolve each through the gate (self-describing prod_data when flagged, else data).
  for (const [propertyId, { data: dataValue, prodData, surveySource }] of latestByPropertyId) {
    const dataTree = parseJson<AttributeField[]>(dataValue, []);
    const { fields } = resolveSurveyAttributes({
      surveySource,
      prodData: (prodData as Record<string, unknown> | null) ?? null,
      data: Array.isArray(dataTree) ? dataTree : [],
    });
    fieldsByPropertyId.set(propertyId, collectSurveyFields(fields));
  }

  return fieldsByPropertyId;
}

function getDeficiencyImageUrls(
  deficiency: RawDeficiency,
  propertyId: string,
  imageContext: DeficiencyImageContext,
): string[] {
  const urls: string[] = [];
  const assetId = deficiency.asset?.asset_id;
  if (assetId) {
    urls.push(...(imageContext.assetPhotosByAssetId.get(assetId) ?? []));
  }

  const surveyFields = imageContext.surveyFieldsByPropertyId.get(propertyId);
  for (const fieldId of getSurveyFieldIds(deficiency)) {
    const field = surveyFields?.get(fieldId);
    const evidence = (field as { evidence?: unknown } | undefined)?.evidence;
    if (Array.isArray(evidence)) {
      urls.push(...evidence.filter((url): url is string => typeof url === "string" && url.trim() !== ""));
    }
  }

  return Array.from(new Set(urls));
}

function getSurveyFieldIds(deficiency: RawDeficiency): string[] {
  const ids = new Set<string>();
  if (deficiency.survey_field_id) ids.add(deficiency.survey_field_id);
  if (Array.isArray(deficiency.additional_survey_field_ids)) {
    deficiency.additional_survey_field_ids.forEach((id) => {
      if (typeof id === "string" && id.length > 0) ids.add(id);
    });
  }
  return [...ids];
}

function collectSurveyFields(fields: AttributeField[]): Map<string, AttributeField> {
  const fieldsById = new Map<string, AttributeField>();

  const walk = (nodes: AttributeField[]) => {
    for (const field of nodes) {
      fieldsById.set(field.id, field);
      if (field.type === "node") {
        walk(field.fields);
      }
    }
  };

  walk(fields);
  return fieldsById;
}

function resolveAssetPhotoUrl(filePath: string | null | undefined): string {
  if (!filePath) return "";
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  const { data } = supabase.storage.from("asset-documents").getPublicUrl(filePath);
  return data?.publicUrl || "";
}

function parseJson<T>(value: Json | null | undefined, fallback: T): T {
  if (!value) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

function formatLabel(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getPropertyLabel(property: PropertyLite | undefined, fallbackId: string): string {
  if (!property) return fallbackId;
  return property.name || property.address || fallbackId.slice(0, 8);
}

function severityOrder(severity: string): number {
  switch (severity.toLowerCase()) {
    case "critical":
      return 0;
    case "high":
      return 1;
    case "medium":
      return 2;
    case "low":
      return 3;
    default:
      return 4;
  }
}

function appendSheet(
  workbook: XLSX.WorkBook,
  requestedName: string,
  rows: string[][],
  widths: number[],
): void {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet["!cols"] = widths.map((wch) => ({ wch }));

  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < rows[r].length; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!worksheet[ref]) continue;

      worksheet[ref].s = {
        alignment: { wrapText: true, vertical: "top" },
      };

      if (r === 0) {
        worksheet[ref].s.font = { color: { rgb: "FFFFFF" }, bold: true };
        worksheet[ref].s.fill = { fgColor: { rgb: "000000" } };
      }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, uniqueSheetName(workbook, requestedName));
}

function uniqueSheetName(workbook: XLSX.WorkBook, requestedName: string): string {
  const baseName = sanitize(requestedName)
    .replace(/[\\/?*[\]:]/g, "")
    .slice(0, 31) || "Sheet";
  let name = baseName;
  let index = 1;

  while (workbook.SheetNames.includes(name)) {
    const suffix = ` (${index})`;
    name = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
    index++;
  }

  return name;
}
